import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import qs from 'qs'
import { generatePath } from 'react-router'

import { TOAST_STR } from '@/constant'
import {
  getMakeVariant,
  getMakeVariantCapabilities,
  makeVariantSuffixFor,
} from '@/constant/makeWorksheet/makeVariantSearch'
import type { MakeVariant } from '@/constant/makeWorksheet/variant'
import type { 학습지필터 } from '@/contentComponents/makeLessonMaterial/(filter)/_deprecated/service/MakeLessonMaterialFilter.service'
import { SupportedWorkbookDetailService } from '@/contentComponents/makeLessonMaterial/(step1)/widget/_deprecated/MakeBySupportedWorkbook/service/SupportedWorkbookDetail.service'
import type { AssignedStudent } from '@/contentComponents/modal/AssignGroupStudentModal/AssignGroupStudentModal'
import AssignGroupStudentModal from '@/contentComponents/modal/AssignGroupStudentModal/AssignGroupStudentModal'
import { derivationController } from '@/controller/derivation.controller'
import { lessonWorksheetController } from '@/controller/lessonWorksheet.controller'
import { worksheetController } from '@/controller/worksheet.controller'
import type { MakeWorksheetPreviewOptionFilter, Student, Worksheet } from '@/dto'
import { MakeWorksheetByChapterOption } from '@/dto'
import {
  MAKE_LESSON_MATERIAL_STEP1_TYPE,
  MakeLessonMaterialCommonParams,
} from '@/dto/LessonMaterial/MakeLessonMaterialCommonParams'
import type { MyList } from '@/dto/MyList/MyList'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import { commonErrorMessage, toastError, toastSuccess } from '@/utils/toast'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'
import worksheetUtil from '@/utils/Worksheet'

import modalService from './modal.service'
import { scenarioService } from './scenario.service'

export type CreateFilterIdParams =
  | {
      type: Extract<res.WorksheetDetail['filter']['type'], 'WEAK_CONCEPT'>
      params: req.CreateFilterByWeakConcept
    }
  | {
      type: Extract<res.WorksheetDetail['filter']['type'], 'WEAK_PERIOD'>
      params: req.CreateFilterByWeakPeriod
    }
  | {
      type: Extract<res.WorksheetDetail['filter']['type'], 'WEAK_WORKSHEET'>
      params: req.CreateFilterByWeakWorksheet
    }
  | {
      type: Extract<res.WorksheetDetail['filter']['type'], 'SCHOOL_TEST'>
      params: req.CreateFilterBySchoolTest
    }
  | {
      type: Extract<res.WorksheetDetail['filter']['type'], 'CUSTOM_PAPER'>
      params: req.CreateFilterByCustomPaper
    }
  | {
      type: Extract<res.WorksheetDetail['filter']['type'], 'SCHOOL_TEST_PAPER'>
      params: req.CreateFilterBySchoolTestPaper
    }
  | {
      type: Extract<res.WorksheetDetail['filter']['type'], 'ONE_CLICK'>
      params: req.CreateFilterByOneClick
    }
  | {
      type: Extract<res.WorksheetDetail['filter']['type'], 'MERGED_WORKSHEET'>
      params: req.CreateFilterByMerge
    }

class CommonWorksheet {
  moveEditWorksheetFromDerivation({
    filterId,
    assignStudentIdList,
    tag,
    prefix,
    designOption,
    problemSortType,
    download,
    entryPath,
    destinationPath,
    originPublicWorkbookId,
    weakWorkbookSourceId,
    weakWorkbookPartitionType,
  }: {
    filterId: string
    assignStudentIdList: string[]
    tag?: MakeWorksheetPreviewOptionFilter['tag']
    prefix: MakeWorksheetPreviewOptionFilter['prefix']
    designOption: Partial<res.WorksheetDetail['pdf']>
    problemSortType?: common.MakeWorksheetProblemSortType
    download?: boolean
    entryPath: MakeLessonMaterialCommonParams<'COPY'>['entryContext']['entryPath']
    destinationPath?: MakeLessonMaterialCommonParams<'COPY'>['entryContext']['destinationPath']
    originPublicWorkbookId?: WorkbookEntity.Workbook['id']
    weakWorkbookSourceId?: WorkbookEntity.Workbook['id']
    weakWorkbookPartitionType?: 'PAGE' | 'PROBLEM'
  }) {
    const queryString = qs.stringify(
      {
        ...designOption,
        tag,
        prefix,
        assignStudentIdList,
      },
      { arrayFormat: 'comma' },
    )

    const params = new MakeLessonMaterialCommonParams<'COPY'>({
      filterId,
      problemSortType,
      download,
      entryContext: {
        entryPath,
        destinationPath,
        fixOption: 'COPY',
      },
      originPublicWorkbookId,
      weakWorkbookSourceId,
      weakWorkbookPartitionType,
    }).params

    commonRepo.router.push(`/make-worksheet/edit/${params}&${queryString}`)
  }

  // 학습지 수정
  // 학습지 필터 정보 get -> filterId생성 -> 학습지 만들기 2단계 이동
  async moveEditWorksheet<FixOption extends common.FixOption>({
    worksheetId,
    // entryPath,
    // fixOption = 'EDIT',
    assignStudentIdList = [],
    designOption = {},
    myListId,
    entryContext,
    makeVariant = 'default',
  }: {
    worksheetId: Worksheet['id'] // 학습지 아이디
    // entryPath: MakeCommonParams<FixOption>['entryPath'] // 1단계 진입 경로
    // fixOption: common.MakeWorksheetFixOption // copy - 복제 후 수정 / edit - 학습지 수정 / EDIT_ASSIGNED - 출제된 학습지 수정(3단계 진입)
    assignStudentIdList?: Student['id'][]
    designOption?: Partial<res.WorksheetDetail['pdf']>
    myListId?: MyList['id']
    entryContext: MakeLessonMaterialCommonParams<FixOption>['entryContext']
    /** entrance-test면 make-flow 저장이 creverse API로 분기된다 */
    makeVariant?: MakeVariant
  }) {
    try {
      const data = await worksheetController.fetchWorksheetDetail(worksheetId)
      const { filter, problems, pdf, title, tag, writer, titlePrefix, school, grade } = data
      const _filter = (() => {
        if (!filter) return new MakeWorksheetByChapterOption()
        return filter
      })()
      const problemList = problems.map(({ id, tagTop, baseWorkbookProblemId, problemSource }) => ({
        id,
        tagTop,
        baseWorkbookProblemId,
        boxIndex: problemSource?.id,
      }))
      const { type, ...filterOption } = _filter

      let source: string | undefined = undefined
      if (entryContext.fixOption !== 'NONE' && entryContext.entryPath === RouteName.practiceTest) {
        source = 'PRACTICE_TEST'
      }

      const params = {
        ...filterOption,
        problemList,
        title,
        source,
      }

      const filterId = await this.createFilterId({ type, params } as CreateFilterIdParams)

      if (entryContext.fixOption === 'EDIT' && assignStudentIdList.length > 0) {
        modalService.openConfirmModal({
          content: (
            <div>
              <p>학생에게 {tJosa('출제')}된 학습지는 수정이 불가능합니다. </p>
              <p>{tJosa('출제')} 취소를 먼저 진행해주세요.</p>
            </div>
          ),
          onClick: modalService.closeModal,
          actionButtonText: '확인',
          hasCancelButton: false,
        })
        return
      }
      const param = new MakeLessonMaterialCommonParams<FixOption>({
        entryContext,
        filterId,
        sameOptionUse: 'NONE',
        filterType: type as common.WorksheetType,
      }).params
      const queryString = qs.stringify(
        {
          title,
          worksheetId,
          tag,
          writer,
          prefix: titlePrefix,
          schoolType: school,
          grade,
          assignStudentIdList,
          ...{ ...designOption, ...pdf },
          myListId,
        },
        { arrayFormat: 'comma' },
      )
      const url =
        entryContext.fixOption === 'EDIT_ASSIGNED'
          ? `/make-worksheet/options`
          : `/make-worksheet/edit`
      const moveToUrl = `${url}/${param}&${queryString}${makeVariantSuffixFor(makeVariant)}`
      commonRepo.router.push(moveToUrl)
    } catch (error) {
      if (error.code === 'WRONG_REQUEST_CURRICULUM_GROUP') {
        toastError('09개정 문제가 포함된 학습지는 생성 또는 수정할 수 없습니다.')
      } else {
        commonErrorMessage(error)
      }
    }
  }

  async createFilterId<T extends res.CommonFilter['type']>(
    props: T extends 'CONCEPT'
      ? { type: 'CONCEPT'; params: 학습지필터.단원유형별필터 }
      : T extends
            | 'PUBLIC_WORKBOOK'
            | 'SCHOOL_WORKBOOK'
            | 'CUSTOM_SIGNATURE_WORKBOOK'
            | 'ACADEMY_GROUP_WORKBOOK'
        ? {
            type:
              | 'PUBLIC_WORKBOOK'
              | 'SCHOOL_WORKBOOK'
              | 'CUSTOM_SIGNATURE_WORKBOOK'
              | 'ACADEMY_GROUP_WORKBOOK'
            params: 학습지필터.지원교재필터
          }
        : T extends 'PRACTICE_TEST'
          ? { type: 'PRACTICE_TEST'; params: 학습지필터.수능모의고사필터 }
          : T extends 'SCHOOL_TEST'
            ? { type: 'SCHOOL_TEST'; params: 학습지필터.학교별기출필터 }
            : T extends 'MERGED_WORKSHEET'
              ? { type: 'MERGED_WORKSHEET'; params: 학습지필터.병합학습지필터 }
              : T extends 'MY_DB_ORIGINAL'
                ? { type: 'MY_DB_ORIGINAL'; params: 학습지필터.나의DB원본필터 }
                : T extends 'MY_DB_SIMILAR'
                  ? { type: 'MY_DB_SIMILAR'; params: 학습지필터.나의DB유사필터 }
                  : CreateFilterIdParams,
  ) {
    let filterId: undefined | string = undefined

    switch (props.type) {
      case 'CONCEPT': {
        // 단원 유형
        filterId = await worksheetController.createFilterIdByChapter(props.params)
        break
      }
      case 'PUBLIC_WORKBOOK': // 시중교재
      case 'SCHOOL_WORKBOOK':
      case 'CUSTOM_SIGNATURE_WORKBOOK':
      case 'ACADEMY_GROUP_WORKBOOK':
        {
          filterId = await worksheetController.postFilterBySupportedWorkbook(props.params)
        }
        break
      case 'PRACTICE_TEST': // 수능 모의고사
        {
          filterId = await worksheetController.createFilterByPracticeTest(props.params)
        }
        break
      case 'WEAK_CONCEPT': {
        filterId = await derivationController.fetchFilterIdByConcept(
          props.params as req.CreateFilterByWeakConcept,
        )
        break
      }
      case 'WEAK_PERIOD': {
        filterId = await derivationController.fetchFilterIdByPeriod(
          props.params as req.CreateFilterByWeakPeriod,
        )
        break
      }
      case 'WEAK_WORKSHEET': {
        const { excludedTopicIds, excludedSubTopicIds, ..._params } = props.params
        filterId = await derivationController.fetchFilterIdByWorksheet(
          _params as req.CreateFilterByWeakWorksheet,
        )
        break
      }
      case 'SCHOOL_TEST': {
        filterId = await worksheetController.createFilterBySchoolTest(
          props.params as req.CreateFilterBySchoolTest,
        )
        break
      }
      case 'CUSTOM_PAPER': {
        filterId = await worksheetController.createFilterByCustomPaper({
          filterId: props.params?.filterId,
          paperId: props.params.paperId,
          selectedPageIndexes: props.params.selectedPageIndexes,
          problemList: props.params.problemList,
          selectedConceptIdList: props.params.selectedConceptIdList,
          selectedLittleChapterIdList: props.params.selectedLittleChapterIdList,
        })
        break
      }
      case 'SCHOOL_TEST_PAPER': {
        filterId = await worksheetController.createFilterBySchoolTestPaper({
          filterId: props.params?.filterId,
          paperId: props.params.paperId,
          selectedPageIndexes: props.params.selectedPageIndexes,
          problemList: props.params.problemList,
          selectedConceptIdList: props.params.selectedConceptIdList,
          selectedLittleChapterIdList: props.params.selectedLittleChapterIdList,
        })
        break
      }

      case 'ONE_CLICK': {
        filterId = await worksheetController.createFilterByOneClick({
          ...props.params,
        } as req.CreateFilterByOneClick)
        break
      }

      case 'MERGED_WORKSHEET': {
        filterId = await worksheetController.createFilterIdByMergedWorksheet({
          filterId: props.params.filterId,
          mergedWorksheetIds: props.params.mergedWorksheetIds,
          onlyAutoScorable: props.params.onlyAutoScorable,
          problems: (
            (
              props.params as req.CreateFilterByMerge & {
                problemList: req.CreateFilterByMerge['problems']
              }
            ).problemList ?? []
          ).map((p) => ({
            id: p.id,
            baseWorkbookProblemId: p.baseWorkbookProblemId,
            tagTop: p.tagTop,
          })),
          problemSortType: props.params.problemSortType,
          title: props.params.title,
        })
        break
      }

      case 'MY_DB_ORIGINAL': {
        filterId = await worksheetController.createOriginalFilterBySchoolTestPaperV2({
          filterId: props.params.filterId,
          title: props.params.title,
          myDbProblemDetailIds: props.params.myDbProblemDetailIds,
          problemList: props.params.problemList,
        })
        break
      }

      case 'MY_DB_SIMILAR': {
        try {
          filterId = await worksheetController.createSimilarFilterBySchoolTestPaperV2({
            filterId: props.params.filterId,
            title: props.params.title,
            myDbProblemDetailIds: props.params.myDbProblemDetailIds,
            problemList: props.params.problemList,
            similarX: props.params.similarX === 0 ? null : props.params.similarX,
            similarLevel: props.params.similarLevel,
            onlyAutoScorable: props.params.onlyAutoScorable,
          })
        } catch (error) {
          if (error.code === 'VALIDATION_FAIL' && error.message?.includes('myDbProblemDetailIds')) {
            toastError('모든 원본 문제가 생성 중입니다.\n잠시 후 다시 시도해주세요.')
          }
          throw error
        }
        break
      }
    }
    return filterId
  }

  onExitMakeWorksheet<FixOption extends common.FixOption>(
    entryContext: MakeLessonMaterialCommonParams<FixOption>['entryContext'],
    sameOptionUse = false, // 동일옵션으로 계속 만들지 유무
    makeComplete = false, // 완전히 생성했는지 여부 - 3단계 생성에만 true, 그 외에는 false - 학습지만들기를 제외한 곳에서 사용
    workbookId?: WorkbookEntity.Workbook['id'],
  ) {
    // 동일 범위로 계속 만들 경우
    const makeVariant = getMakeVariant(window.location.search)
    const searchParams = qs.stringify(
      {
        makeComplete: makeComplete ? 'DONE' : 'YET',
        ...(sameOptionUse ? { sameOptionUse: 'USE' } : {}),
        entryPath: entryContext.entryPath,
        destinationPath: entryContext.destinationPath,
        // 재진입 search를 여기서 새로 조립하므로, 빠뜨리면 다음 만들기가 default 모드로 떨어진다
        ...(makeVariant !== 'default' ? { makeVariant } : {}),
      },
      { addQueryPrefix: true },
    )

    SupportedWorkbookDetailService.destroy({
      reason: 'ON_CREATE',
      sameOptionUse,
    })

    if (entryContext.fixOption === 'NONE' && sameOptionUse) {
      switch (entryContext.stepOneType) {
        // 학습지만들기 1단계 - 단원 유형별로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.단원유형별:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByChapter),
            search: searchParams,
          })
          break
        // 학습지만들기 1단계 - 시중교재로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.시중교재:
          if (workbookId) {
            commonRepo.router.push({
              pathname: generatePath(RouteName.makeWorksheetByWorkbookDetail, {
                workbookId,
              }),
              search: searchParams,
            })
          }
          break
        // 학습지만들기 1단계 - 수능 모의고사로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.수능모의고사:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByPracticeTest),
            search: searchParams,
          })
          break
        // 학습지만들기 1단계 - 시그니처 교재로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.시그니처교재:
          if (workbookId) {
            commonRepo.router.push({
              pathname: generatePath(RouteName.makeWorksheetBySignatureWorkbookDetail, {
                workbookId,
              }),
              search: searchParams,
            })
          }
          break
        // 학습지만들기 1단계 - 학교별 내신 기출로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.학교별기출:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetBySchoolTest),
            search: searchParams,
          })
          break
        // 학습지만들기 1단계 - 기출업로드로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.기출업로드:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByPaper),
            search: searchParams,
          })
          break
        default:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByChapter),
            search: searchParams,
          })
      }
      return
      // TODO: 즐겨찾는문제 sameoptionuse안쓰는지 한번더 체크
    }

    // 동일범위가 아닌경우
    const _pathname =
      entryContext.destinationPath ||
      entryContext.entryPath ||
      RouteName[getMakeVariantCapabilities(window.location.search).exitFallbackRouteName]
    const [pathname = '', search = ''] = _pathname.split('?')

    commonRepo.router.push({
      pathname: generatePath(pathname),
      search: search,
    })
  }
  // 학습지 일괄 출제
  assignStudentWorksheetList = (
    worksheetList: Worksheet[],
    fetchInitCallback?: (
      worksheetIdList: Worksheet['id'][],
      assignedStudentIds?: Worksheet['assignedStudentIdList'],
      unassignedStudentIds?: Worksheet['assignedStudentIdList'],
    ) => Promise<void> | void,
    onAssignClickTracking?: () => void,
  ) => {
    const worksheetIdList = worksheetList.map((worksheet) => worksheet.id)

    // 선택한 학습지들에서 공통으로 출제된 학생들의 리스트
    const getCommonAssignedLists = (worksheetList: Worksheet[]) => {
      const noDuplicatedAssignedList: AssignedStudent[] = []
      const duplicatedAssignedList: AssignedStudent[] = []

      const assignLists = worksheetList.map((worksheet) => {
        if (worksheet.assignedStudentWorksheetList) {
          return worksheet.assignedStudentWorksheetList
        }
      })

      assignLists.flat().forEach((assignedStudent) => {
        if (assignedStudent) {
          if (
            !noDuplicatedAssignedList
              .map((assigned) => assigned.studentId)
              .includes(assignedStudent.studentId)
          ) {
            noDuplicatedAssignedList.push(assignedStudent)
          }
        }
      })

      noDuplicatedAssignedList.forEach((unique) => {
        const duplicated = assignLists
          .flat()
          .filter((assign) => assign?.studentId === unique.studentId)
        if (duplicated.length === worksheetList.length) {
          duplicatedAssignedList.push(unique)
        }
      })

      const duplicatedAssignedIdList = duplicatedAssignedList.map((item) => item.studentId)
      const commonAssignedLists: AssignedStudent[][] = []

      assignLists.forEach((studentList) => {
        const assignArr: AssignedStudent[] = []
        studentList?.forEach((student) => {
          if (duplicatedAssignedIdList.includes(student.studentId)) {
            assignArr.push(student)
          }
        })
        commonAssignedLists.push(assignArr)
      })

      return commonAssignedLists
    }

    modalService.openModal(
      <AssignGroupStudentModal
        title={`학습지 일괄 ${tJosa('출제')}하기`}
        titleDescription={`선택한 학습지를 학생들에게 일괄 ${tJosa('출제')}해보세요.`}
        assignedLists={getCommonAssignedLists(worksheetList)}
        onSubmit={async (studentList) => {
          try {
            onAssignClickTracking?.()
            await lessonWorksheetController.assignStudentWorksheet(
              studentList.map(({ id }) => id),
              worksheetIdList,
            )
            fetchInitCallback?.(worksheetIdList)
            toastSuccess(`시험지가 ${tJosa('출제')}되었습니다.`)
            worksheetUtil.isThemeWorksheetTag(worksheetList[0].tag) &&
              _mixpanelUtils.uiTrack('테마별학습지_일괄출제', {
                학습지수: worksheetIdList.length,
              })
          } catch (error) {
            commonErrorMessage(error)
          }
        }}
        onUnassign={async (newlyAssignedStudentIds) => {
          await lessonWorksheetController.deleteStudentWorksheets(newlyAssignedStudentIds)
          fetchInitCallback?.(worksheetIdList)
          modalService.closeModal()
          toastSuccess(`시험지가 ${tJosa('출제')} 취소되었습니다.`)
        }}
      />,
    )
  }

  // 학습지 개별 출제
  assignStudentWorksheet = (
    worksheet: Worksheet,
    fetchInitCallback?: (
      worksheetId: Worksheet['id'],
      assignedStudentIds?: Worksheet['assignedStudentIdList'],
      unassignedStudentIds?: Worksheet['assignedStudentIdList'],
    ) => Promise<void> | void,
    onAssignClickTracking?: () => void,
  ) => {
    modalService.openModal(
      <AssignGroupStudentModal
        title={`학습지 ${tJosa('출제')}하기`}
        titleDescription={worksheet.title}
        assignedLists={[worksheet.assignedStudentWorksheetList || []]}
        onSubmit={async (assignedStudents) => {
          try {
            onAssignClickTracking?.()
            await lessonWorksheetController.assignStudentWorksheet(
              assignedStudents.map(({ id }) => id),
              [worksheet.id],
            )

            scenarioService.postScenarioAndLoadCompletePopupHanlder('학습지출제하기')

            fetchInitCallback?.(
              worksheet.id,
              assignedStudents.map(({ id }) => id),
              [],
            )
            toastSuccess(TOAST_STR.assignWorksheet)
            _mixpanelUtils.uiTrack('테마별학습지_출제하기', {
              학제: worksheet.schoolType,
              출판사: worksheet?.publisher ?? null,
              학생수: assignedStudents.length,
            })
          } catch (error) {
            if (error.status === 400) {
              modalService.openDeletedBookModal()
            } else {
              commonErrorMessage(error)
            }
          }
        }}
        onUnassign={async (newlyAssignedStudentIds, studentIds) => {
          try {
            await lessonWorksheetController.deleteStudentWorksheets(newlyAssignedStudentIds)
            fetchInitCallback?.(worksheet.id, [], studentIds)
            toastSuccess(TOAST_STR.unassignWorksheet)
            modalService.closeModal()
          } catch (error) {
            if (error.status === 400) {
              modalService.openDeletedBookModal()
            } else {
              commonErrorMessage(error)
            }
          }
        }}
      />,
      {
        onClose: () => {
          fetchInitCallback?.(worksheet.id, [], [])
          return true
        },
      },
    )
  }
}

export const commonWorksheetService = new CommonWorksheet()
